import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSettings, createOrUpdateHeaderSettings, toggleDropdown, toggleNavItem } from "../api/settingsApi";
import toast from "react-hot-toast";
import { useEffect } from "react";

export const useSettings = () => {
  const queryClient = useQueryClient();

  // Fetch settings
  const { data: settings, isLoading, isError } = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  });

  // Mutation for creating or updating settings
  const mutation = useMutation(
      {
    mutationFn :({ data, logoFile }) => createOrUpdateHeaderSettings(data, logoFile),
      onSuccess: () => {
        toast.success("Settings updated successfully");
        queryClient.invalidateQueries(["settings"]); // Refetch settings after update
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update settings");
      },
    }
  );

  // Mutation for toggling dropdown item status
  const toggleDropdownMutation = useMutation({
    mutationFn: (id) => toggleDropdown(id),
    onSuccess: () => {
      // Invalidate after a short delay to sync with server, but let optimistic update show first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["settings"] });
      }, 500);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to toggle dropdown item status");
    },
  });

  // Mutation for toggling nav item status
  const toggleNavItemMutation = useMutation({
    mutationFn: (id) => toggleNavItem(id),
    onSuccess: () => {
      // Invalidate after a short delay to sync with server, but let optimistic update show first
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["settings"] });
      }, 500);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to toggle nav item status");
    },
  });

  useEffect(() => {
    if (isError) {
      toast.error("Failed to fetch settings");
    }
  }, [isError]);

  return {
    settings,
    isLoading,
    updateSettings: mutation.mutate,
    isUpdating: mutation.isPending,
    toggleDropdown: toggleDropdownMutation.mutateAsync,
    toggleNavItem: toggleNavItemMutation.mutateAsync,
    isTogglingDropdown: toggleDropdownMutation.isPending,
    isTogglingNavItem: toggleNavItemMutation.isPending,
  };
};